import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap'
import Helmet from 'react-helmet'

import { UserProfileService } from '../services/UserProfileService'

import { Loading } from './common/Loading'

import './UserProfile.scss'

const UserProfile = () => {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [key, setKey] = useState('profile')

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    setLoading(true)
    try {
      const data = await UserProfileService.getProfile()
      setProfile(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await UserProfileService.updateProfile(profile)
      setProfile(updated)
      // Success toast?
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }))
  }

  const handlePrefChange = (category, channel, value) => {
    setProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [category]: {
          ...prev.preferences[category],
          [channel]: value,
        },
      },
    }))
  }

  if (loading) return <Loading />

  return (
    <div className="component-UserProfile container mt-4">
      <Helmet title="User Profile" />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>User Profile</h2>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs activeKey={key} onSelect={k => setKey(k)} className="mb-4">
        <Tab eventKey="profile" title="Contact Information">
          <Form>
            <Form.Group as={Row}>
              <Form.Label column sm="2">
                Email
              </Form.Label>
              <Col sm="10">
                <Form.Control plaintext readOnly defaultValue={profile.email} />
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label column sm="2">
                First Name
              </Form.Label>
              <Col sm="10">
                <Form.Control
                  type="text"
                  value={profile.firstName}
                  onChange={e => handleChange('firstName', e.target.value)}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label column sm="2">
                Last Name
              </Form.Label>
              <Col sm="10">
                <Form.Control
                  type="text"
                  value={profile.lastName}
                  onChange={e => handleChange('lastName', e.target.value)}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label column sm="2">
                Mobile Phone
              </Form.Label>
              <Col sm="10">
                <Form.Control
                  type="text"
                  placeholder="+1 (555) 000-0000"
                  value={profile.phoneNumber || ''}
                  onChange={e => handleChange('phoneNumber', e.target.value)}
                />
                <Form.Text className="text-muted">Required for SMS notifications. Format: +1 (xxx) xxx-xxxx</Form.Text>
              </Col>
            </Form.Group>
          </Form>
        </Tab>

        <Tab eventKey="notifications" title="Notification Preferences">
          <p className="text-muted mb-4">
            Manage how you want to be notified. Note: Urgent Alerts cannot be disabled for In-App.
          </p>

          <table className="table table-bordered">
            <thead className="thead-light">
              <tr>
                <th>Notification Type</th>
                <th className="text-center">In-App</th>
                <th className="text-center">Email</th>
                <th className="text-center">SMS</th>
              </tr>
            </thead>
            <tbody>
              {renderPrefRow(
                'Appointments & Tasks',
                'appointmentsAndTasks',
                profile.preferences.appointmentsAndTasks,
                handlePrefChange,
              )}
              {renderPrefRow('Case Report Notifications', 'reports', profile.preferences.reports, handlePrefChange)}
              {renderPrefRow('Urgent Alerts', 'urgentAlerts', profile.preferences.urgentAlerts, handlePrefChange)}
              {renderPrefRow('Messages (Chat)', 'chat', profile.preferences.chat, handlePrefChange)}
            </tbody>
          </table>
        </Tab>
      </Tabs>
    </div>
  )
}

const renderPrefRow = (label, key, prefs, onChange) => (
  <tr key={key}>
    <td>{label}</td>
    <td className="text-center">
      <Form.Check
        type="checkbox"
        checked={prefs?.inApp ?? true}
        onChange={e => onChange(key, 'inApp', e.target.checked)}
        disabled={key === 'urgentAlerts'} // Enforce Urgent Alerts?
      />
    </td>
    <td className="text-center">
      <Form.Check
        type="checkbox"
        checked={prefs?.email ?? true}
        onChange={e => onChange(key, 'email', e.target.checked)}
      />
    </td>
    <td className="text-center">
      <Form.Check
        type="checkbox"
        checked={prefs?.sms ?? false}
        onChange={e => onChange(key, 'sms', e.target.checked)}
      />
    </td>
  </tr>
)

export default UserProfile
